import { ShieldCheck, ShieldAlert, CheckCircle2, Clock, AlertTriangle } from "lucide-react";

const CURRENT_CONTROLS = [
  { label: "JWT-based authentication", note: "server.py — token issued on login/register, verified on every protected route" },
  { label: "Password hashing (bcrypt)", note: "server.py — passwords never stored in plaintext" },
  { label: "Password-reset workflow", note: "server.py — time-limited reset token via email" },
  { label: "Role-based project access", note: "collab.py — owner/editor/viewer enforced server-side, not just hidden in the UI" },
  { label: "Protected API routes", note: "Bearer auth required on nearly every backend endpoint" },
  { label: "CORS restrictions", note: "server.py — explicit allowed-origins list, not a wildcard" },
  { label: "Secrets kept outside source control", note: ".env is gitignored; no credentials committed to the repo" },
  { label: "Generic user-facing error messages", note: "internal errors (e.g. a paused database) never leak implementation details to the UI" },
  { label: "Backend failure logging", note: "structured logging on exceptions and warnings throughout the API" },
  { label: "Input validation", note: "Pydantic models validate request shape on essentially every endpoint" },
  { label: "Database connection pooling", note: "db_pg.py — bounded connection pool, not one raw connection per request" },
  { label: "HTTPS", note: "provided by the hosting layer (Render) — not something this codebase implements itself" },
];

const LIMITATIONS = [
  "JWT sessions have no server-side revocation — a captured token remains valid until its 30-day expiry. Logout is client-side only today.",
  "File uploads to the Knowledge Base have no size limit, no real content-type verification (filename extension only), and no malware scanning.",
  "No rate limiting exists on any endpoint yet.",
  "No security headers (CSP, HSTS, X-Frame-Options) are set beyond CORS.",
  "Semantic/vector document search has not been independently tested for cross-project data isolation under the newer multi-collaborator model.",
];

const ROADMAP: { phase: string; items: string[] }[] = [
  {
    phase: "Phase 1 — Foundation",
    items: [
      "Centralized authorization review across all endpoints",
      "Tenant/organization isolation testing",
      "Email verification on signup",
      "Secure session revocation (replace client-only logout)",
      "Rate limiting",
      "Security headers (CSP, HSTS, etc.)",
      "Dependency and secret scanning in CI",
    ],
  },
  {
    phase: "Phase 2 — Verification",
    items: [
      "Cross-project access testing",
      "Role-escalation testing",
      "Password-reset abuse testing",
      "File-upload validation and scanning",
      "WebSocket authorization testing",
      "Vector-search tenant isolation testing",
      "Backup restoration testing",
    ],
  },
  {
    phase: "Phase 3 — Production Readiness",
    items: [
      "External security review",
      "Focused penetration testing",
      "Incident-response plan",
      "Vulnerability-reporting process",
      "Legal and privacy review",
      "Production monitoring and alerting",
    ],
  },
];

export default function SecurityPage() {
  return (
    <div className="max-w-3xl mx-auto px-6 md:px-8 py-12 md:py-16">
      {/* Header */}
      <div className="mb-12">
        <div className="flex items-center gap-3 mb-6">
          <ShieldCheck className="w-6 h-6 text-primary" />
          <span className="text-xs uppercase tracking-widest text-muted-foreground font-body">
            Security Posture
          </span>
        </div>
        <h1 className="text-4xl font-display font-bold tracking-tight mb-4">
          What's actually true, and what isn't yet
        </h1>
        <p className="text-base text-muted-foreground font-body leading-relaxed">
          This page treats security as part of product design, not an afterthought. It lists only controls that
          are verified in the codebase — and is equally direct about what's still a gap. baneuro.org is a
          prototype and demonstration platform: it is not intended for production customer data, regulated
          information, or sensitive client documents. Future production use would require a formal security,
          privacy, legal, and operational readiness review.
        </p>
      </div>

      {/* Current environment */}
      <div className="mb-12 rounded-lg border border-border bg-secondary/20 p-5">
        <h2 className="text-sm font-mono uppercase tracking-wider text-muted-foreground mb-3">Current Environment</h2>
        <dl className="grid grid-cols-2 gap-3 text-sm">
          <dt className="text-muted-foreground">Stage</dt><dd className="font-semibold">Prototype</dd>
          <dt className="text-muted-foreground">Data classification</dt><dd className="font-semibold">Demo and test data only</dd>
          <dt className="text-muted-foreground">Production customer data</dt><dd className="font-semibold">Not accepted</dd>
          <dt className="text-muted-foreground">External security certification</dt><dd className="font-semibold">None</dd>
          <dt className="text-muted-foreground">External penetration test</dt><dd className="font-semibold">Not yet completed</dd>
        </dl>
      </div>

      {/* Current controls */}
      <div className="mb-12">
        <h2 className="text-lg font-display font-bold mb-4 flex items-center gap-2">
          <CheckCircle2 className="w-5 h-5 text-emerald-600" /> Current Controls
        </h2>
        <div className="space-y-2">
          {CURRENT_CONTROLS.map((c) => (
            <div key={c.label} className="flex gap-3 p-3 rounded-md border border-border bg-card">
              <CheckCircle2 className="w-4 h-4 text-emerald-600 shrink-0 mt-0.5" />
              <div>
                <p className="text-sm font-semibold text-card-foreground">{c.label}</p>
                <p className="text-xs text-muted-foreground mt-0.5">{c.note}</p>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Limitations */}
      <div className="mb-12">
        <h2 className="text-lg font-display font-bold mb-4 flex items-center gap-2">
          <AlertTriangle className="w-5 h-5 text-amber-600" /> Current Limitations
        </h2>
        <div className="space-y-2">
          {LIMITATIONS.map((l) => (
            <div key={l} className="flex gap-3 p-3 rounded-md border border-amber-200 bg-amber-50">
              <AlertTriangle className="w-4 h-4 text-amber-600 shrink-0 mt-0.5" />
              <p className="text-sm text-amber-900">{l}</p>
            </div>
          ))}
        </div>
      </div>

      {/* Roadmap */}
      <div className="mb-12">
        <h2 className="text-lg font-display font-bold mb-4 flex items-center gap-2">
          <Clock className="w-5 h-5 text-accent" /> Planned Security Roadmap
        </h2>
        <div className="space-y-4">
          {ROADMAP.map((r) => (
            <div key={r.phase} className="rounded-lg border border-border bg-card p-4">
              <p className="text-sm font-semibold text-card-foreground mb-2">{r.phase}</p>
              <ul className="space-y-1">
                {r.items.map((item) => (
                  <li key={item} className="text-xs text-muted-foreground flex gap-2">
                    <span className="text-muted-foreground/50">—</span> {item}
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      </div>

      {/* Disclosure + limitation notice */}
      <div className="border-t border-border pt-8 space-y-4">
        <div className="flex gap-3">
          <ShieldAlert className="w-4 h-4 text-muted-foreground shrink-0 mt-0.5" />
          <p className="text-xs text-muted-foreground leading-relaxed">
            A formal vulnerability-reporting channel will be established before any production launch.
          </p>
        </div>
        <p className="text-xs text-muted-foreground leading-relaxed">
          No public software system can be guaranteed free of vulnerabilities. The current objective is to reduce
          risk, limit exposure, document known limitations, and verify controls before any production use.
        </p>
      </div>
    </div>
  );
}
